import { useMemo, useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { getOldAssetsAttachmentState, getOldAssets } from "store/selectors";
import {
  getCountryCode,
  getCurrentLanguage,
  formatDate,
  getAWSDate,
  getPreviousDaysDate,
  today,
  getDigitalCampaignContactInfo,
  formatAssetMetadata,
  getDateFormat,
} from "../services/common";
import { Formik, Form as FormikForm, FieldArray } from "formik";
import * as yup from "yup";

import {
  Checkbox,
  Grid,
  Button,
  Icon,
  List,
  Transition,
  Segment,
  Divider,
  Popup,
  Message,
} from "semantic-ui-react";
import "react-semantic-ui-datepickers/dist/react-semantic-ui-datepickers.css";
import _ from "lodash";
import AssetSelectionModal from "components/AssetSelectionModal/AssetSelectionModal";
import { useTranslation } from "react-i18next";
import FileInput from "components/FileInput/FileInput";
import CampaignInfo from "components/CampaignInfo/CampaignInfo";
import LoadResolver from "components/LoadResolver";
import { toast } from "components/Toast/Toast";
import { ErrorInfo, ErrorText, FieldRow } from "components/form";
import { fileToImgSrc, replaceArrayItem } from "services/utils";
import { CampaignType } from "models";
import { ACTIVE_CAMPAIGN_STATUSES, CAMPAIGN_ALREADY_CHECKED } from "constants/campaignStatuses";
import { STEPS } from "store/models/deliverNew";
import ConfirmationModal from "components/ConfirmationModal/ConfirmationModal";
import AssetStatusInfo from "components/AssetStatusInfo/AssetStatusInfo";
import AssetImage from "components/AssetImage/AssetImage";

const checkFileName = (fileName) => {
  const nonValidCharacters = new RegExp(/[^a-zA-Z0-9_.-]/g);
  return fileName.replace(nonValidCharacters, "");
};

const parseFileName = (fileName) => {
  const lastDot = fileName.lastIndexOf(".");
  if (lastDot <= 0) {
    return { name: fileName, ext: "" };
  }
  return {
    name: fileName.slice(0, lastDot),
    ext: fileName.slice(lastDot),
  };
};

const fileToAsset = async (file, initialAssetProps = {}) => {
  const { name, ext } = parseFileName(file.name);
  const newAsset = new File([file], checkFileName(name), {
    type: file.type,
  });
  const newFile = {
    file: newAsset,
    id: _.uniqueId("newAsset"),
    name: newAsset.name,
    type: file.type,
    extension: ext,
    imgSrc: file.localAssetLocation || (await fileToImgSrc(file)),
    isNew: true,
    ...initialAssetProps,
  };
  return newFile;
};
// Checks if the editable data in the asset has been changed
const isAssetInfoChanged = (updatedAsset, oldAsset) => {
  if (!updatedAsset || !oldAsset) return false;
  const lowResolutionChanged =
    Object.prototype.hasOwnProperty.call(updatedAsset, "lowResolution") &&
    Boolean(updatedAsset.lowResolution) !==
      Boolean(oldAsset.lowResolution ?? _.get(oldAsset, "metadata.lowResolution"));

  return (
    updatedAsset.name !== oldAsset.name ||
    updatedAsset.start_time !== oldAsset.start_time ||
    updatedAsset.end_time !== oldAsset.end_time ||
    updatedAsset.week_days !== oldAsset.week_days ||
    updatedAsset.notes !== oldAsset.notes ||
    lowResolutionChanged
  );
};

function DeliveryForm(props) {
  const {
    onSuccess,
    goPrev,
    assets,
    processedAssets,
    campaign: campaignProp,
    dispatch,
    deliveredAssetResolutions,
    deliveredImageAssetResolutions,
    isAdmin,
  } = props;
  const [showConfirmation, setShowConfirmation] = useState(false); // For ConfirmationModal
  const [dataForSubmit, setDataForSubmit] = useState(null); // For ConfirmationModal
  // Campaign gotten as prop is saved to component's state since the asset load below will make campaign undefined in CampaignDisplay component
  // eslint-disable-next-line no-unused-vars
  const [campaign, setCampaign] = useState(campaignProp || {});
  const [campaignType, setCampaignType] = useState(campaign.type);
  const [campaignNotes, setCampaignNotes] = useState(campaign.notes);
  useEffect(() => dispatch.oldAssets.load(), []);
  const { isLoading, error, items } = useSelector(getOldAssets);

  const { attachmentDownloads, overallProgress } = useSelector(getOldAssetsAttachmentState);

  const activeUser = useSelector((state) => state.activeUser.item) || {};
  const { t } = useTranslation("common");

  const deliverNewState = useSelector((state) => state.deliverNew);

  const assetsByCampaignType = useMemo(
    () =>
      campaignType === CampaignType.PRINT
        ? items.filter((value) => {
            return value.type === "application/pdf";
          })
        : items.filter((value) => {
            return value.type !== "application/pdf";
          }),
    [items, campaignType]
  );
  const [userAssetsByCampaingType, setUserAssetsByCampaignType] = useState(assetsByCampaignType);
  useEffect(() => setUserAssetsByCampaignType(assetsByCampaignType), [assetsByCampaignType]);
  const [campaignHasAssets, setCampaignHasAssets] = useState();
  // eslint-disable-next-line no-unused-vars

  const [attachingProcessError, setAttachingProcessError] = useState(false);
  const [confirmationModalOpen, setAssetSelectionModalOpen] = useState(false);
  const [isNewAssetLoading, setIsNewAssetLoading] = useState(false);
  const [fileNameWarnings, setFileNameWarnings] = useState({});
  const fileNameWarningTimeoutsRef = useRef({});
  const [confirmationModalData, setAssetSelectionModalData] = useState({
    text: "",
    headerText: "",
    icon: "",
  });

  const [newCampaignAssets, setNewCampaignAssets] = useState(assets || []);
  const [assetIdsToBeDeleted, setAssetIdsToBeDeleted] = useState([]);

  const [handleAssetId, setHandleAssetId] = useState(undefined);
  const defaultNewAssetProps = useMemo(
    () =>
      campaignType === CampaignType.PRINT && campaign.lowResolution ? { lowResolution: true } : {},
    [campaign.lowResolution, campaignType]
  );

  const newAssetsByCreationOrder = (a, b) => {
    return a.isNew && !b.isNew ? -1 : a.isNew && b.isNew ? 0 : 1;
  };

  const orderedAssets = newCampaignAssets.sort(newAssetsByCreationOrder);

  const handleAssetFieldChange = (name, value, id) => {
    const nameSplit = name.split(".");
    const field = nameSplit[nameSplit.length - 1]; // Last value from name split is the field
    const newValue = value;

    const oldAsset = _.find(newCampaignAssets, ["id", id]);
    if (oldAsset) {
      const newAsset =
        field === "lowResolution" && !newValue
          ? _.omit(oldAsset, "lowResolution")
          : { ...oldAsset, [field]: newValue };
      const newAssets = replaceArrayItem(
        newCampaignAssets,
        (asset) => asset.id === newAsset.id,
        newAsset
      );
      setNewCampaignAssets(newAssets);
    }
  };

  const handleModifyAsset = () => {
    const { assetId, action } = handleAssetId;
    const updateSelected = userAssetsByCampaingType.map((asset) => {
      const newObjectProperties =
        action === "click"
          ? {
              isSelected: !attachingProcessError && !asset.isSelected ? true : !asset.isSelected,
            }
          : { isDownloaded: action === "removeAttached" ? false : true, isSelected: false };

      const modifiedAsset =
        asset.id === assetId
          ? {
              ...asset,
              ...newObjectProperties,
            }
          : asset;
      return modifiedAsset;
    });
    return updateSelected;
  };

  useEffect(() => {
    if (handleAssetId) {
      const newAssets = handleModifyAsset();
      setUserAssetsByCampaignType(newAssets);
      setHandleAssetId(undefined);
    }
  }, [handleAssetId, attachingProcessError]);

  const handleRemoveFromAttachmentList = (assetName) => {
    const findAssetId = userAssetsByCampaingType.filter((asset) => {
      return asset.name === assetName && asset.isDownloaded;
    });
    setHandleAssetId({ assetId: findAssetId[0].id, action: "removeAttached" });
  };

  const showFileNameWarning = (assetId) => {
    setFileNameWarnings((prev) => ({ ...prev, [assetId]: { isFading: false } }));

    if (fileNameWarningTimeoutsRef.current[assetId]) {
      clearTimeout(fileNameWarningTimeoutsRef.current[assetId].fadeTimeoutId);
      clearTimeout(fileNameWarningTimeoutsRef.current[assetId].hideTimeoutId);
    }

    const fadeTimeoutId = setTimeout(() => {
      setFileNameWarnings((prev) => {
        if (!prev[assetId]) return prev;
        return {
          ...prev,
          [assetId]: { isFading: true },
        };
      });
    }, 3000);

    const hideTimeoutId = setTimeout(() => {
      setFileNameWarnings((prev) => {
        const next = { ...prev };
        delete next[assetId];
        return next;
      });
      delete fileNameWarningTimeoutsRef.current[assetId];
    }, 5000);

    fileNameWarningTimeoutsRef.current[assetId] = { fadeTimeoutId, hideTimeoutId };
  };

  useEffect(() => {
    return () => {
      Object.values(fileNameWarningTimeoutsRef.current).forEach(
        ({ fadeTimeoutId, hideTimeoutId }) => {
          clearTimeout(fadeTimeoutId);
          clearTimeout(hideTimeoutId);
        }
      );
    };
  }, []);

  const initialValues = useMemo(
    () => ({
      campaignType: campaignType || "",
      campaignNotes: campaignNotes || "",
      assets: newCampaignAssets.map((asset) => ({
        ...asset,
        metadata: asset.metadata || {},
        lowResolution: Boolean(asset.lowResolution ?? _.get(asset, "metadata.lowResolution")),
        start_time: asset.start_time ? new Date(asset.start_time) : undefined,
        end_time: asset.end_time ? new Date(asset.end_time) : undefined,
      })),
    }),
    [newCampaignAssets, campaignType, campaignNotes]
  );

  const schema = yup.object().shape({
    campaignType: yup.mixed().oneOf(["PRINT", "DIGITAL"]).required(t("required")),
    assets: yup.array().of(
      yup.object().shape({
        name: yup.string().required(t("required")),
        type: yup.string().required(t("required")),
      })
    ),
  });
  const handleRequestsEvents = (e, n, data) => {
    // disabling es-lint because there is no need for a default case
    // eslint-disable-next-line default-case
    switch (e.type) {
      case "loadstart":
        setIsNewAssetLoading(true);
        dispatch.attachmentProgress.addAttachmentDownload(data, 0);
        dispatch.attachmentProgress.loadStart();
        break;
      case "error":
        setAttachingProcessError(true);
        data.isErrored = true;
        break;
      case "progress":
        data.size = e.total;
        dispatch.attachmentProgress.addAttachmentDownload(data, e.loaded);
        break;
      case "loadend":
        if (e.loaded !== 0 || e.loaded < e.total) {
          setHandleAssetId({ assetId: data.id, action: "newAttachment" });
          toast(undefined, t("asset_loaded_success", { assetName: data.name }));
        }
        break;
    }
  };
  /* eslint-enable */
  const addListeners = (xhr, n, assetData) => {
    xhr.addEventListener("loadstart", (e) => handleRequestsEvents(e, n, assetData));
    xhr.addEventListener("loadend", (e) => handleRequestsEvents(e, n, assetData));
    xhr.addEventListener("progress", (e) => handleRequestsEvents(e, n, assetData));
    xhr.addEventListener("error", (e) => handleRequestsEvents(e, n, assetData));
  };

  let newAssetRequest = (assetData) => {
    return new Promise((resolve, reject) => {
      let xhr = new XMLHttpRequest();
      xhr.open("GET", assetData.url, assetData, true);
      xhr.setRequestHeader("fileId", assetData.assetId);
      addListeners(xhr, assetData.assetNumber, assetData);
      xhr.responseType = "blob";
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          const blob = new Blob([xhr.response], {
            type: assetData.type,
          });
          const dateAdded = new Date().getTime();
          let fileReader = new FileReader();
          fileReader.onload = function (evt) {
            let result = evt.target.result;
            const file = new File([blob], assetData.name, {
              path: assetData.name,
              lastModified: dateAdded,
              lastModifiedDate: new Date(dateAdded),
              size: parseInt(xhr.getResponseHeader("content-length")),
              type: assetData.type,
              localAssetLocation: result,
            });
            try {
              fileToAsset(file, defaultNewAssetProps).then((res) => {
                setCampaignHasAssets("newAssets", res);
                setAttachingProcessError(false);
                res.fromList = true;
                setNewCampaignAssets((assets) => [res, ...assets]);
              });
            } catch (error) {
              setAttachingProcessError(true);
            }
          };
          fileReader.readAsDataURL(blob);
          resolve(xhr.response);
        } else {
          reject(xhr.statusText);
        }
      };
      xhr.onerror = () => reject(xhr.statusText);
      xhr.send();
    });
  };

  const attachNewAssets = (newAssetsList) => {
    newAssetsList.forEach((asset) => {
      newAssetRequest({
        url: asset.mediaUrl,
        type: asset.type,
        media: asset.media,
        name: asset.name,
        id: asset.id,
      })
        .then((data) => {})
        .catch((error) => {
          setAttachingProcessError(true);
          console.error(error);
        });
    });
  };

  /**
   * Formik uses this function onSubmit, show ConfirmationModal to user if Campaign.status is SCHELUDED or SENT_TO_PRINT
   * @param {*} campaign - Campaign from props => used for status check
   * @param {*} values - Formik form fields
   */
  const handleSubmit = (campaign, values) => {
    setDataForSubmit(values);
    if (CAMPAIGN_ALREADY_CHECKED.includes(campaign.status || campaign.automaticStatus)) {
      setShowConfirmation(true);
    } else {
      submit(values);
    }
  };
  const submit = (values) => {
    try {
      const assetActions = [];
      const processedAssetIds = assetIdsToBeDeleted
        .map((id) => processedAssets.filter((item) => item.media.includes(id)))
        .flat()
        .map((item) => item.id);

      const combinedAssetIdsToBeDeleted = [...assetIdsToBeDeleted, ...processedAssetIds];

      if (values.campaignNotes !== campaign.notes || values.campaignType !== campaign.type) {
        const updateProps = {
          id: campaign.id,
          type: values.campaignType,
          notes: values.campaignNotes,
        };
        assetActions.push({ type: "update-campaign", props: updateProps });
      }
      // Make actions for creating and updating assets
      for (let i = 0; i < values.assets.length; i++) {
        const asset = values.assets[i];

        if (asset.isNew) {
          // Create new
          const file = asset.file;
          const finalName = asset.name + asset.extension;
          const createProps = {
            name: finalName,
            type: asset.type,
            status: "ARRIVED",
            thumbnailComplete: false,
            campaignID: campaign.id,
            start_time: asset.start_time ? getAWSDate(asset.start_time) : null,
            end_time: asset.end_time ? getAWSDate(asset.end_time) : null,
            week_days: asset.week_days,
            notes: asset.notes,
            media: "temp",
            file,
            mediaCollaborators: campaign.mediaCollaborators || [],
            ...(asset.lowResolution ? { lowResolution: true } : {}),
          };
          assetActions.push({ type: "create", props: createProps });
        } else {
          const oldAsset = _.find(assets, ["id", asset.id]);
          const updateProps = {
            id: asset.id,
            name: asset.name,
            type: asset.type,
            start_time: asset.start_time ? getAWSDate(asset.start_time) : null,
            end_time: asset.end_time ? getAWSDate(asset.end_time) : null,
            week_days: asset.week_days,
            notes: asset.notes,
          };

          // Only update asset if it has been changed
          if (isAssetInfoChanged(updateProps, oldAsset))
            assetActions.push({ type: "update", props: updateProps });
        }
      }
      // Make actions for deleting assets from the campaign (i.e. change asset's campaignID to null)
      for (const assetId of processedAssets && processedAssets.length === 0
        ? assetIdsToBeDeleted
        : combinedAssetIdsToBeDeleted) {
        assetActions.push({
          type: "update",
          props: { id: assetId, campaignID: null },
          removeFromCampaign: true,
        });
      }

      onSuccess(assetActions);
    } catch (error) {
      console.error(error);
      toast("error", t("asset_update_error"));
    }
  };

  const handleOpenAssetsInModalWindow = (_e) => {
    setAssetSelectionModalData({
      text: t("add_campaing_assets_modal_text"),
      headerText: t("add_campaing_assets_modal_header"),
    });
    setAssetSelectionModalOpen(true);
  };

  const isCampaignActive = useMemo(() => _.includes(ACTIVE_CAMPAIGN_STATUSES, campaign.status), [
    campaign.status,
  ]);

  const getAssetEndTimeMinDate = (values, index) => {
    const assetStartTime = _.get(values, "assets." + index + ".start_time");
    return assetStartTime
      ? new Date(assetStartTime)
      : getPreviousDaysDate(new Date(campaign.start_time));
  };

  const isOwner = campaign && activeUser && activeUser.username === campaign.owner;
  const countryCode = getCountryCode();

  return (
    <>
      <ConfirmationModal
        data={{
          headerText: t("asset_sent_to_print_header"),
          text: t("asset_sent_to_print_p1"),
          text2:
            t("asset_sent_to_print_contact_digital") +
            " " +
            getDigitalCampaignContactInfo(countryCode),
          text3: t("asset_sent_to_print_p2"),

          // CLEARCH-702 TLE
          //text4:
          //  t("asset_sent_to_print_contact_print") + " " + getPrintCampaignContactInfo(countryCode),
        }}
        open={showConfirmation}
        setOpen={setShowConfirmation}
        onSuccess={submit}
        dataForOnSuccess={dataForSubmit}
      />
      <CampaignInfo
        campaign={campaign}
        activeUser={activeUser}
        isAdmin={isAdmin}
        isOwner={isOwner}
        deliveredAssetResolutions={deliveredAssetResolutions}
        deliveredImageAssetResolutions={deliveredImageAssetResolutions}
      />
      <Divider section />
      {/* Warning messages for contributors */}
      {campaignType && !activeUser.isAdmin && (
        <>
          {getAWSDate(today) > campaign.end_time && (
            <Message warning>{t("delivery_form_campaign_ended")}</Message>
          )}
          {!isCampaignActive && <Message warning>{t("delivery_form_campaign_inactive")}</Message>}
        </>
      )}
      {!campaign.type && (
        <>
          <div>
            {!campaignType ? (
              <p>{t("delivery_form_chose_campaign_type")}</p>
            ) : campaignType && campaignHasAssets ? (
              <div style={{ marginBottom: "1.5rem" }}>
                <p>
                  {t("delivery_form_new_campaign_chosen", {
                    newCampaignType: campaignType,
                  })}
                </p>
                <p>{t("delivery_form_change_campaign_type_indication")}</p>
              </div>
            ) : (
              <p>
                {t("delivery_form_new_campaign_chosen", {
                  newCampaignType: campaignType,
                })}
              </p>
            )}
          </div>

          <Button.Group
            className={`campaign-type-select ${campaignHasAssets ? "disabled" : "enabled"}`}
          >
            <Button
              data-test="print_or_button"
              type="button"
              onClick={() => {
                setCampaignType(CampaignType.PRINT);
                setNewCampaignAssets((assets) => assets.filter((a) => !a.isNew));
              }}
              active={campaignType === CampaignType.PRINT}
            >
              Print (PDF)
            </Button>
            <Button.Or />
            <Button
              data-test="digital_or_button"
              type="button"
              onClick={() => {
                setCampaignType(CampaignType.DIGITAL);
                setNewCampaignAssets((assets) => assets.filter((a) => !a.isNew));
              }}
              active={campaignType === CampaignType.DIGITAL}
            >
              Digital (mp4, mov, jpg, png)
            </Button>
          </Button.Group>
        </>
      )}
      {campaignType &&
        (activeUser.isAdmin ||
          (getAWSDate(today) <= campaign.end_time && isCampaignActive) || // Contributors can only send new assets if campaign has not ended and is active
          !campaign.end_time) && (
          <>
            <h3>{t("delivery_form_assets")}</h3>
            <label htmlFor="temp">{t("delivery_form_next_step_information")}</label>
            <div className="assets-selection">
              <div className="new-assets">
                <h4>{t("add_files_from_computer")}</h4>
                <p> {t("delivery_form_file_upload_instructions")} </p>
                <FileInput
                  onChange={async (newFiles) => {
                    const realFiles = newFiles.filter((file) => !file.name.startsWith("._"));
                    if (realFiles.length !== newFiles.length) {
                      console.warn("Ignored macOS metadata files (._*)");
                      toast("error", t("asset_upload_error"));
                    }
                    const newAssets = await Promise.all(
                      realFiles.map((file) => fileToAsset(file, defaultNewAssetProps))
                    );
                    setCampaignHasAssets("newAssets", newAssets);
                    setNewCampaignAssets((assets) => [...newAssets, ...assets]);
                  }}
                  accept={
                    campaignType === CampaignType.PRINT
                      ? "application/pdf"
                      : "image/jpeg, video/mp4, image/png, video/quicktime, application/zip, application/x-zip-compressed, multipart/x-zip"
                  }
                  // onError={...} // TODO implement
                />
              </div>
              <div className="new-assets">
                <h4>{t("add_files_from_campaign")}</h4>
                <p>{t("add_campaing_assets_modal_text")}</p>
                {
                  <div style={{ marginTop: "auto" }}>
                    <LoadResolver isLoading={isLoading || !campaignType} error={error}>
                      {
                        <Button
                          className="file-input"
                          title={t("open_modal_window")}
                          onClick={() => handleOpenAssetsInModalWindow()}
                          fluid
                          size="large"
                          style={{
                            color: "black",
                            backgroundColor: "#f7f7f7",
                            border: "1px solid #c7c7c7",
                            height: "5rem",
                          }}
                        >
                          <Icon name="window restore outline" size="big" />
                          {t("open_modal_window")}
                        </Button>
                      }
                    </LoadResolver>
                  </div>
                }
              </div>
            </div>
          </>
        )}
      <Divider />
      <Formik
        validationSchema={schema}
        initialValues={initialValues}
        onSubmit={(values) => handleSubmit(campaign, values)}
        enableReinitialize
        validateOnChange={false}
      >
        {({ values = {}, errors, submitCount }) => (
          <FormikForm>
            {/* Campaign notes field is shown to contributors when delivering to an existing campaign */}
            {!activeUser.isAdmin && deliverNewState.step === STEPS.CREATE_ASSETS && (
              <Grid columns={2} verticalAlign="middle" style={{ margin: "1em 0" }}>
                <FieldRow
                  label={t("delivery_form_campaign_notes")}
                  name="campaignNotes"
                  value={values.campaignNotes}
                  type="area"
                  onChange={(_e, { value }) => setCampaignNotes(value)}
                  style={{ marginBottom: "-5px" }}
                >
                  <span className="help-text">{t("delivery_form_campaign_notes_help_text")}</span>
                </FieldRow>
              </Grid>
            )}
            <FieldArray
              name="assets"
              render={(arrayHelpers) => {
                return (
                  <Transition.Group as={List} duration={200} divided verticalAlign="middle">
                    {orderedAssets.map((asset, index) => (
                      <Segment.Group key={index}>
                        {asset.status && (
                          <AssetStatusInfo
                            assetStatusData={{
                              assetStatus: asset.status.toLowerCase(),
                              assetMetaData: formatAssetMetadata(
                                asset["metadata"],
                                campaign.duration || []
                              ),
                              assetKey: asset.id,
                            }}
                          />
                        )}
                        <Segment key={asset.id} className="asset-input-item">
                          <Grid columns={2}>
                            <Grid.Row>
                              <Grid.Column
                                width={6}
                                verticalAlign="middle"
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  flexDirection: "column",
                                  gap: "1rem",
                                }}
                              >
                                <AssetImage asset={asset} />
                              </Grid.Column>
                              <Grid.Column width={9} verticalAlign="middle">
                                <Grid columns={2} verticalAlign="middle">
                                  <FieldRow
                                    label={t("asset_name")}
                                    name={`assets.${index}.name`}
                                    onChange={(_e, { value }) => {
                                      const newValue = checkFileName(value);
                                      if (value !== newValue) {
                                        showFileNameWarning(asset.id);
                                      }
                                      handleAssetFieldChange("name", newValue, asset.id);
                                    }}
                                    onBlur={(e) => {
                                      const value = e.target.value;
                                      const newValue = checkFileName(value);
                                      handleAssetFieldChange("name", newValue, asset.id);
                                    }}
                                  >
                                    {fileNameWarnings[asset.id] && (
                                      <div
                                        className="file-name-warning-text"
                                        style={{
                                          color: "#9f3a38",
                                          display: "flex",
                                          alignItems: "center",
                                          gap: "0.35rem",
                                          marginTop: "0.35rem",
                                          opacity: fileNameWarnings[asset.id].isFading ? 0 : 1,
                                          transition: "opacity 300ms ease",
                                        }}
                                      >
                                        <Icon name="warning circle" color="yellow" />
                                        <span>{t("delivery_form_file_name_error")}</span>
                                      </div>
                                    )}
                                  </FieldRow>
                                  <ErrorText name={"assets." + index + ".name"} />
                                  <Grid.Row className="form-field-row">
                                    <Grid.Column width={3} textAlign="right">
                                      {t("asset_type")}
                                    </Grid.Column>
                                    <Grid.Column width={13}>
                                      {asset.type.indexOf("/") > -1
                                        ? asset.type.split("/")[1].toUpperCase()
                                        : asset.type.toUpperCase()}
                                    </Grid.Column>
                                  </Grid.Row>
                                  {asset.resolution && (
                                    <Grid.Row className="form-field-row">
                                      <Grid.Column width={3} textAlign="right">
                                        {t("asset_resolution")}
                                      </Grid.Column>
                                      <Grid.Column width={13}>{asset.resolution}</Grid.Column>
                                    </Grid.Row>
                                  )}
                                  <ErrorText name={"assets." + index + ".type"} />
                                  {(campaignType || values.campaignType) === CampaignType.PRINT && (
                                    <Grid.Row className="form-field-row">
                                      <Grid.Column width={3} textAlign="right" />
                                      <Grid.Column width={13}>
                                        <Checkbox
                                          id={`assets.${index}.low_resolution`}
                                          label={t("add_print_asset_lowres_name")}
                                          disabled={!asset.isNew}
                                          checked={Boolean(
                                            _.get(values, `assets.${index}.lowResolution`)
                                          )}
                                          onChange={(_e, { checked }) =>
                                            handleAssetFieldChange(
                                              "lowResolution",
                                              Boolean(checked),
                                              values.assets[index].id
                                            )
                                          }
                                        />
                                        <Popup
                                          content={t("add_print_asset_lowres_hover_text")}
                                          trigger={
                                            <Icon
                                              name="info circle"
                                              style={{
                                                marginLeft: "0.5rem",
                                                cursor: "pointer",
                                              }}
                                            />
                                          }
                                          position="top center"
                                        />
                                      </Grid.Column>
                                    </Grid.Row>
                                  )}
                                  <FieldRow
                                    label={t("asset_start_time")}
                                    name={"assets." + index + ".start_time"}
                                    type="date"
                                    locale={getCurrentLanguage()}
                                    firstDayOfWeek={1}
                                    format={getDateFormat()}
                                    autoComplete="off"
                                    // Weird bug with react-semantic-ui-datepickers where minDate has to be one day before the day you want to be selectable
                                    minDate={getPreviousDaysDate(new Date(campaign.start_time))}
                                    maxDate={new Date(campaign.end_time)}
                                    placeholder={formatDate(campaign.start_time)}
                                    disabled={!campaign.start_time || !campaign.end_time}
                                    clearable={campaign.start_time && campaign.end_time}
                                    onChange={(_e, { name, value }) =>
                                      handleAssetFieldChange(name, value, values.assets[index].id)
                                    }
                                  />
                                  <FieldRow
                                    label={t("asset_end_time")}
                                    name={"assets." + index + ".end_time"}
                                    type="date"
                                    locale={getCurrentLanguage()}
                                    firstDayOfWeek={1}
                                    format={getDateFormat()}
                                    autoComplete="off"
                                    minDate={getAssetEndTimeMinDate(values, index)}
                                    maxDate={new Date(campaign.end_time)}
                                    placeholder={formatDate(campaign.end_time)}
                                    disabled={!campaign.start_time || !campaign.end_time}
                                    clearable={campaign.start_time && campaign.end_time}
                                    onChange={(_e, { name, value }) =>
                                      handleAssetFieldChange(name, value, values.assets[index].id)
                                    }
                                  />
                                  {(campaignType || values.campaignType) ===
                                    CampaignType.DIGITAL && (
                                    <FieldRow
                                      label={t("asset_week_days")}
                                      name={"assets." + index + ".week_days"}
                                      type="weekdays"
                                      onChange={(name, value) =>
                                        handleAssetFieldChange(name, value, values.assets[index].id)
                                      }
                                    />
                                  )}
                                  <FieldRow
                                    label={t("asset_notes")}
                                    name={"assets." + index + ".notes"}
                                    data-test="asset_notes_input"
                                    onChange={(e) =>
                                      handleAssetFieldChange("notes", e.target.value, asset.id)
                                    }
                                  />
                                </Grid>
                              </Grid.Column>
                              <Grid.Column width={1} verticalAlign="top">
                                <Popup
                                  content={t("asset_delete_popup")}
                                  trigger={
                                    <Button
                                      data-test="delete_asset_from_campaign_button"
                                      floated="right"
                                      compact
                                      size="mini"
                                      type="button"
                                      icon="close"
                                      onClick={() => {
                                        if (
                                          newCampaignAssets[index].isNew &&
                                          newCampaignAssets[index].fromList
                                        ) {
                                          handleRemoveFromAttachmentList(
                                            newCampaignAssets[index].name
                                          );
                                        }
                                        arrayHelpers.remove(index);
                                        setCampaignHasAssets(values.assets.length - 1);
                                        const newAssets = _.filter(
                                          newCampaignAssets,
                                          (existingAsset) => asset.id !== existingAsset.id
                                        );
                                        setNewCampaignAssets(newAssets);
                                        // Only set asset to be deleted if it was an existing asset and not newly created
                                        if (!asset.isNew)
                                          setAssetIdsToBeDeleted((assets) =>
                                            assets.concat(asset.id)
                                          );
                                      }}
                                    />
                                  }
                                  position="top center"
                                />
                              </Grid.Column>
                            </Grid.Row>
                          </Grid>
                        </Segment>
                      </Segment.Group>
                    ))}
                  </Transition.Group>
                );
              }}
            />
            <ErrorText name="assets" />
            <ErrorInfo name={errors} submitCount={submitCount} t={t} tPrefix={"asset_"} />

            <div className="form-footer">
              {goPrev && (
                <Button type="button" primary onClick={goPrev}>
                  {t("delivery_form_back_button")}
                </Button>
              )}
              <Button
                primary
                type="submit"
                disabled={assetIdsToBeDeleted.length === 0 && values.assets.length === 0}
                data-test="delivery_form_submit_button"
              >
                {t("delivery_form_submit_button")}
              </Button>
            </div>
          </FormikForm>
        )}
      </Formik>
      <AssetSelectionModal
        data={confirmationModalData}
        open={confirmationModalOpen}
        overallProgress={overallProgress}
        attachmentDownloads={attachmentDownloads}
        attachingProcessError={attachingProcessError}
        isNewAssetLoading={isNewAssetLoading}
        handleSetClickedAssetId={(assetId) =>
          setHandleAssetId({ assetId: assetId, action: "click" })
        }
        attachNewAssets={attachNewAssets}
        setOpen={setAssetSelectionModalOpen}
        assets={userAssetsByCampaingType}
        closeIcon
        cancelButtonText={t("modal_cancel_button_text")}
        maxNumberOfAssets={12}
      />
    </>
  );
}

export default DeliveryForm;
